import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, CreditCard, User, Palette, Key, Check, Loader2 as SettingsLoader, Eye, EyeOff, Bell, Send, Mail, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import DashboardShell from '../components/DashboardShell';
import WhiteLabelSettingsPanel from '../components/WhiteLabelSettingsPanel';
import { formatApiDetail, parseResponseJson } from '../lib/http';

const CATEGORY_LABELS = {
  ai: { label: 'AI & LLM', color: 'from-violet-500 to-purple-500' },
  payments: { label: 'Payments', color: 'from-green-500 to-emerald-500' },
  platform_backend: { label: 'Platform backend (superadmin)', color: 'from-amber-500 to-orange-500' },
  communications: { label: 'Communications', color: 'from-blue-500 to-cyan-500' },
  google: { label: 'Google Services', color: 'from-red-500 to-orange-500' },
  email: { label: 'Email', color: 'from-pink-500 to-rose-500' },
  voice: { label: 'Voice AI', color: 'from-indigo-500 to-blue-500' },
  booking: { label: 'Booking', color: 'from-teal-500 to-cyan-500' },
  domains: { label: 'Domain Registration', color: 'from-amber-500 to-yellow-500' },
  social_media: { label: 'Social Media Publishing', color: 'from-sky-500 to-blue-600' },
  ads: { label: 'Ads launch', color: 'from-fuchsia-500 to-rose-500' },
  analytics: { label: 'Web analytics', color: 'from-cyan-500 to-teal-500' },
};

/** Map readiness deep-links (?focus=) onto Settings API key categories. */
const FOCUS_TO_CATEGORY = {
  ai: 'ai',
  llm: 'ai',
  openai: 'ai',
  email: 'email',
  smtp: 'email',
  sendgrid: 'email',
  social: 'social_media',
  social_media: 'social_media',
  ads: 'ads',
  meta: 'ads',
  google_ads: 'ads',
  analytics: 'analytics',
  plausible: 'analytics',
  ga4: 'analytics',
  payments: 'payments',
  stripe: 'payments',
  platform_backend: 'platform_backend',
  hunter: 'platform_backend',
  serper: 'platform_backend',
  apify: 'platform_backend',
  tavily: 'platform_backend',
  linkedin: 'platform_backend',
  communications: 'communications',
};

const API_BASE = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== '')
  ? process.env.REACT_APP_BACKEND_URL
  : '';

const TIDB_BUILDER_KEY_NAMES = new Set([
  'DATABASE_URL',
  'TIDB_CLOUD_DATABASE_ENDPOINT',
  'GITHUB_TOKEN',
  'VERCEL_TOKEN',
]);

const ApiKeysPanel = () => {
  const [searchParams] = useSearchParams();
  const focusParam = (searchParams.get('focus') || '').trim().toLowerCase();
  const focusCategory = FOCUS_TO_CATEGORY[focusParam] || (CATEGORY_LABELS[focusParam] ? focusParam : '');
  const [keys, setKeys] = useState([]);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [showKeys, setShowKeys] = useState({});
  const [testing, setTesting] = useState({});
  const [testResults, setTestResults] = useState({});
  const [canManageBilling, setCanManageBilling] = useState(false);
  const [billingScope, setBillingScope] = useState(null);
  const [billingStatus, setBillingStatus] = useState(null);
  const focusApplied = useRef('');

  useEffect(() => {
    fetchKeys();
  }, []);

  useEffect(() => {
    if (!loaded || !focusCategory || focusApplied.current === focusCategory) return undefined;
    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-api-keys-category="${focusCategory}"]`);
      if (!el) return;
      focusApplied.current = focusCategory;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.classList.add('ring-2', 'ring-violet-500/70');
      setTimeout(() => el.classList.remove('ring-2', 'ring-violet-500/70'), 2400);
    }, 120);
    return () => clearTimeout(timer);
  }, [loaded, focusCategory, keys]);

  const fetchKeys = async () => {
    setLoadError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/settings/api-keys`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCanManageBilling(!!data.can_manage_billing);
        setBillingScope(data.billing_scope || null);
        setBillingStatus(data.billing_status || null);
        const visibleKeys = (data.keys || []).filter(
          (k) => k.category !== 'tidb_builder' && !TIDB_BUILDER_KEY_NAMES.has(k.key),
        );
        setKeys(visibleKeys);
        const vals = {};
        visibleKeys.forEach(k => { vals[k.key] = ''; });
        setValues(vals);
      } else {
        const data = await res.json().catch(() => ({}));
        setLoadError(data.detail || `Failed to load API keys (${res.status})`);
      }
    } catch (e) {
      console.error('Failed to load API keys:', e);
      setLoadError('Could not reach the backend. Is the server running?');
    } finally {
      setLoaded(true);
    }
  };

  const handleSave = async () => {
    const updates = {};
    Object.entries(values).forEach(([k, v]) => {
      if (v && !v.startsWith('••')) updates[k] = v;
    });
    if (Object.keys(updates).length === 0) {
      toast.error('No changes to save');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/settings/api-keys`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(updates),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Saved ${data.updated?.length || 0} API key(s)`);
        setValues(prev => {
          const next = { ...prev };
          (data.updated || []).forEach(k => { next[k] = ''; });
          return next;
        });
        fetchKeys();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || 'Failed to save');
      }
    } catch (e) {
      toast.error('Failed to save API keys');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (provider) => {
    setTesting(prev => ({ ...prev, [provider]: true }));
    setTestResults(prev => ({ ...prev, [provider]: null }));
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/settings/api-keys/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ provider }),
      });
      const data = await res.json();
      setTestResults(prev => ({ ...prev, [provider]: { ok: !!data.ok, message: data.message || '' } }));
      if (data.ok) toast.success(data.message || 'Connection OK');
      else toast.error(data.message || 'Connection failed');
    } catch (e) {
      setTestResults(prev => ({ ...prev, [provider]: { ok: false, message: 'Request failed' } }));
      toast.error('Test request failed');
    } finally {
      setTesting(prev => ({ ...prev, [provider]: false }));
    }
  };

  const grouped = {};
  keys.forEach(k => {
    const cat = k.category || 'other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(k);
  });

  if (!loaded) return <div className="flex items-center gap-2 text-zinc-400 p-8"><SettingsLoader className="w-4 h-4 animate-spin" /> Loading...</div>;

  if (loadError) {
    return (
      <div className="glass p-8 rounded-2xl">
        <h2 className="text-2xl font-heading font-bold mb-2">API Keys & Integrations</h2>
        <p className="text-red-400 text-sm mb-4">{loadError}</p>
        <Button onClick={fetchKeys} variant="outline" className="border-zinc-700">Retry</Button>
      </div>
    );
  }

  return (
    <div className="glass p-8 rounded-2xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-heading font-bold">API Keys & Integrations</h2>
          <p className="text-zinc-400 text-sm mt-1">Configure external service credentials for your platform</p>
          <Link
            to="/platform-guide?section=api-setup"
            className="inline-flex items-center gap-1 mt-2 text-xs text-sky-400 hover:text-sky-300 underline"
          >
            Open API setup knowledge base →
          </Link>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-violet-600 hover:bg-violet-500">
          {saving ? <SettingsLoader className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      <div className="space-y-6">
        {canManageBilling && billingStatus && (
          <div className={`rounded-xl border px-4 py-3 text-sm ${
            billingScope === 'agency'
              ? 'border-sky-800/60 bg-sky-950/40 text-sky-100/90'
              : 'border-emerald-800/60 bg-emerald-950/40 text-emerald-100/90'
          }`}>
            <p className={`font-medium ${billingScope === 'agency' ? 'text-sky-300' : 'text-emerald-300'}`}>
              {billingScope === 'agency' ? 'Agency billing gateways' : 'Platform billing gateways'}
            </p>
            <p className={`mt-1 text-xs ${billingScope === 'agency' ? 'text-sky-200/80' : 'text-emerald-200/80'}`}>
              {billingScope === 'agency'
                ? 'Stripe and PhonePe keys below are what your white-label clients pay through for plans and credits. Save your agency branding first if save fails.'
                : 'Stripe and PhonePe keys below are what end users pay through for plans and credits. They are stored separately from each user’s own API keys.'}
            </p>
            {billingScope === 'agency' && (
              <p className="mt-1 text-xs text-zinc-400">
                Stripe webhook URL: {'{BACKEND}'}/api/webhook/stripe/agency/{'{your_user_id}'}
              </p>
            )}
            <p className="mt-2 text-xs text-zinc-400">
              Stripe: {billingStatus.stripe_configured ? `ready (${billingStatus.stripe_source})` : 'not configured'}
              {' · '}
              PhonePe: {billingStatus.phonepe_configured ? `ready (${billingStatus.phonepe_source})` : 'not configured'}
            </p>
          </div>
        )}
        {Object.entries(grouped).map(([cat, catKeys]) => {
          const meta = CATEGORY_LABELS[cat] || { label: cat, color: 'from-gray-500 to-gray-600' };
          const isGatewayPayments = cat === 'payments' && catKeys.some((k) => k.admin_only);
          const isPlatformBackend = cat === 'platform_backend';
          const paymentsTitle = billingScope === 'agency'
            ? 'Client payments (Stripe & PhonePe)'
            : 'Platform payments (Stripe & PhonePe)';
          const paymentsHint = billingScope === 'agency'
            ? 'White-label admin — your clients checkout via these gateways'
            : 'Admin only — users checkout for Snagcart plans/credits via these gateways';
          return (
            <div
              key={cat}
              data-api-keys-category={cat}
              className={`border rounded-xl overflow-hidden transition-shadow ${
                focusCategory === cat ? 'border-violet-500/70' : 'border-zinc-800'
              }`}
            >
              <div className={`px-4 py-3 bg-gradient-to-r ${meta.color} bg-opacity-10`}>
                <h3 className="font-semibold text-sm">
                  {isGatewayPayments ? paymentsTitle : meta.label}
                </h3>
                {isGatewayPayments && (
                  <p className="text-xs text-white/70 mt-0.5">
                    {paymentsHint}
                  </p>
                )}
                {isPlatformBackend && (
                  <p className="text-xs text-white/70 mt-0.5">
                    Superadmin only — platform infrastructure keys. Hidden from all other users.
                  </p>
                )}
              </div>
              <div className="p-4 space-y-4">
                {catKeys.map(k => (
                  <div key={k.key} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">{k.label}</Label>
                      <div className="flex items-center gap-2">
                        {(k.platform_admin_only || k.admin_only) && (
                          <span className="text-xs bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full">
                            {k.platform_admin_only
                              ? 'Superadmin'
                              : billingScope === 'agency' ? 'Agency' : 'Admin'}
                          </span>
                        )}
                        {k.is_set && (
                          <span className="text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">
                            {k.source === 'agency' ? 'Agency configured'
                              : k.source === 'platform' ? 'Platform configured'
                              : k.source === 'user' ? 'User configured' : 'Environment'}
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => setShowKeys(prev => ({ ...prev, [k.key]: !prev[k.key] }))}
                          className="text-zinc-500 hover:text-zinc-300"
                        >
                          {showKeys[k.key] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-zinc-500">{k.description}</p>
                    <Input
                      data-testid={`settings-api-key-input-${k.key.toLowerCase().replace(/_/g, '-')}`}
                      type={showKeys[k.key] ? 'text' : 'password'}
                      placeholder={k.is_set ? k.value : `Enter ${k.label}...`}
                      value={values[k.key] || ''}
                      onChange={e => setValues(prev => ({ ...prev, [k.key]: e.target.value }))}
                      className="bg-zinc-900 border-zinc-700 focus:border-violet-500 text-sm"
                    />
                    {k.test_provider && (
                      <div className="flex items-center gap-2 pt-1">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => handleTest(k.test_provider)}
                          disabled={testing[k.test_provider]}
                          className="h-7 px-3 text-xs border-zinc-700"
                          data-testid={`test-${k.test_provider}`}
                        >
                          {testing[k.test_provider] ? <SettingsLoader className="w-3 h-3 animate-spin mr-1" /> : null}
                          Test connection
                        </Button>
                        {testResults[k.test_provider] && (
                          <span className={`text-xs ${testResults[k.test_provider].ok ? 'text-green-400' : 'text-red-400'}`}>
                            {testResults[k.test_provider].ok ? '✓ ' : '✗ '}{testResults[k.test_provider].message}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Settings = ({ user, setUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const backendUrl = API_BASE;
  const requestedTab = new URLSearchParams(location.search).get('tab');
  const VALID_TABS = ['profile', 'api-keys', 'privacy', 'whitelabel', 'support-notifications'];
  const defaultTab = VALID_TABS.includes(requestedTab) ? requestedTab : 'profile';

  // ── Billing state ──────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState('basic');

  // ── Profile state ──────────────────────────────────────────────────────────
  const [profileName, setProfileName] = useState(user?.name || '');
  const [savingProfile, setSavingProfile] = useState(false);

  // ── Password state ─────────────────────────────────────────────────────────
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [billingBusy, setBillingBusy] = useState(false);
  const [billingSummary, setBillingSummary] = useState(null);

  // Sync profileName when user prop arrives
  useEffect(() => {
    if (user?.name) setProfileName(user.name);
  }, [user?.name]);

  useEffect(() => {
    const loadBilling = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${backendUrl}/api/subscription/billing/summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) setBillingSummary(await res.json());
      } catch {
        /* ignore */
      }
    };
    loadBilling();
  }, [backendUrl]);

  // ── Profile save ───────────────────────────────────────────────────────────
  const handleSaveProfile = async () => {
    if (!profileName.trim()) { toast.error('Name cannot be empty.'); return; }
    setSavingProfile(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/auth/me/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: profileName }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed to save');
      toast.success('Profile updated!');
      if (setUser) setUser(prev => ({ ...prev, name: profileName }));
    } catch (e) {
      toast.error(e.message || 'Failed to save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  // ── Password change ────────────────────────────────────────────────────────
  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword) { toast.error('Please enter your current password.'); return; }
    if (newPassword.length < 8) { toast.error('New password must be at least 8 characters.'); return; }
    if (newPassword !== confirmPassword) { toast.error('New passwords do not match.'); return; }
    setSavingPassword(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/auth/me/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed to change password');
      toast.success('Password changed successfully!');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (e) {
      toast.error(e.message || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  // ── Checkout ───────────────────────────────────────────────────────────────
  const handleCheckout = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const hostUrl = window.location.origin;
      const response = await fetch(`${backendUrl}/api/payments/checkout/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          package_id: selectedPackage,
          host_url: hostUrl,
          success_url: `${hostUrl}/settings?session_id={CHECKOUT_SESSION_ID}`,
          cancel_url: `${hostUrl}/settings`,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.url) {
        window.location.href = data.url;
      } else {
        toast.error(data.detail || 'Failed to start checkout. Please add your Stripe API key in Settings → API Keys.');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Check for successful payment return
  React.useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get('session_id');
    if (sessionId) checkPaymentStatus(sessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkPaymentStatus = async (sessionId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${backendUrl}/api/payments/checkout/status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await response.json();
      if (data.payment_status === 'paid') {
        toast.success('Payment successful! Your plan has been upgraded.');
        // Re-fetch user so plan badge updates
        const meRes = await fetch(`${backendUrl}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (meRes.ok && setUser) setUser(await meRes.json());
        window.history.replaceState({}, '', '/settings');
      }
    } catch (error) {
      console.error('Error checking payment status');
    }
  };

  return (
    <DashboardShell
      user={user}
      title="Settings"
      subtitle="Manage your account, plan, API keys and white-label configuration"
      activeNav="settings"
    >
    <div className="px-6 py-6 max-w-5xl" data-testid="settings-page">
      <div>
        {/* Header (kept compact since shell already shows breadcrumb) */}
        <div className="mb-6">
          <Button
            onClick={() => navigate('/dashboard')}
            data-testid="back-button"
            variant="ghost"
            className="text-zinc-400 hover:text-white -ml-3"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>

        <Tabs defaultValue={defaultTab} className="space-y-6">
          <TabsList className="bg-zinc-900 p-1 rounded-xl">
            <TabsTrigger value="profile" data-testid="tab-profile" className="data-[state=active]:bg-violet-500">
              <User className="w-4 h-4 mr-2" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="api-keys" data-testid="tab-api-keys" className="data-[state=active]:bg-violet-500">
              <Key className="w-4 h-4 mr-2" />
              API Keys
            </TabsTrigger>
            <TabsTrigger value="privacy" data-testid="tab-privacy" className="data-[state=active]:bg-violet-500">
              <Shield className="w-4 h-4 mr-2" />
              Privacy & Billing
            </TabsTrigger>
            {(user?.snagcart_plan === 'growth' || user?.is_platform_admin) && (
              <TabsTrigger value="whitelabel" data-testid="tab-whitelabel" className="data-[state=active]:bg-violet-500">
                <Palette className="w-4 h-4 mr-2" />
                White-label
              </TabsTrigger>
            )}
            {(user?.snagcart_plan === 'growth' || user?.is_platform_admin) && (
              <TabsTrigger value="support-notifications" data-testid="tab-support-notifications" className="data-[state=active]:bg-violet-500">
                <Bell className="w-4 h-4 mr-2" />
                Support Notifications
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="profile" data-testid="profile-content">
            <div className="space-y-6">
              {/* Profile Information */}
              <div className="glass p-8 rounded-2xl">
                <h2 className="text-2xl font-heading font-bold mb-6">Profile Information</h2>
                <div className="space-y-5 max-w-md">
                  <div>
                    <Label htmlFor="name" className="text-white mb-2 block">Display Name</Label>
                    <Input
                      id="name"
                      value={profileName}
                      onChange={e => setProfileName(e.target.value)}
                      data-testid="name-input"
                      className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 rounded-xl h-12 text-white"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-white mb-2 block">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      defaultValue={user?.email}
                      data-testid="email-input"
                      disabled
                      className="bg-zinc-900/50 border-zinc-800 rounded-xl h-12 text-zinc-500"
                    />
                    <p className="text-sm text-zinc-400 mt-2">Email cannot be changed</p>
                  </div>
                  <Button
                    data-testid="save-profile-button"
                    onClick={handleSaveProfile}
                    disabled={savingProfile}
                    className="bg-white text-black hover:bg-zinc-200 rounded-full px-8 py-3 font-bold flex items-center gap-2"
                  >
                    {savingProfile ? <SettingsLoader className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                    {savingProfile ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>

              {/* Change Password */}
              <div className="glass p-8 rounded-2xl">
                <h2 className="text-2xl font-heading font-bold mb-2">Change Password</h2>
                <p className="text-zinc-400 text-sm mb-6">Update your account password. You will need to enter your current password to confirm.</p>
                <form onSubmit={handleChangePassword} className="space-y-5 max-w-md">
                  <div>
                    <Label className="text-white mb-2 block">Current Password</Label>
                    <div className="relative">
                      <Input
                        type={showCurrentPw ? 'text' : 'password'}
                        value={currentPassword}
                        onChange={e => setCurrentPassword(e.target.value)}
                        placeholder="Enter current password"
                        className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 rounded-xl h-12 text-white pr-11"
                      />
                      <button type="button" onClick={() => setShowCurrentPw(v => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
                        {showCurrentPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-white mb-2 block">New Password</Label>
                    <div className="relative">
                      <Input
                        type={showNewPw ? 'text' : 'password'}
                        value={newPassword}
                        onChange={e => setNewPassword(e.target.value)}
                        placeholder="At least 8 characters"
                        className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 rounded-xl h-12 text-white pr-11"
                      />
                      <button type="button" onClick={() => setShowNewPw(v => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
                        {showNewPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-white mb-2 block">Confirm New Password</Label>
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={e => setConfirmPassword(e.target.value)}
                      placeholder="Repeat new password"
                      className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 rounded-xl h-12 text-white"
                    />
                    {confirmPassword && newPassword !== confirmPassword && (
                      <p className="text-xs text-red-400 mt-1">Passwords do not match</p>
                    )}
                  </div>
                  <Button
                    type="submit"
                    disabled={savingPassword}
                    className="bg-violet-600 hover:bg-violet-500 rounded-full px-8 py-3 font-bold flex items-center gap-2"
                  >
                    {savingPassword ? <SettingsLoader className="w-4 h-4 animate-spin" /> : <Key className="w-4 h-4" />}
                    {savingPassword ? 'Updating...' : 'Update Password'}
                  </Button>
                </form>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="api-keys">
            <ApiKeysPanel />
          </TabsContent>

          <TabsContent value="privacy" data-testid="privacy-content">
            <div className="space-y-6">
              <div className="glass p-8 rounded-2xl space-y-4">
                <h2 className="text-2xl font-heading font-bold">Billing</h2>
                <p className="text-sm text-zinc-400">
                  Plan: <span className="text-white font-medium">{billingSummary?.plan || user?.snagcart_plan || 'basic'}</span>
                  {billingSummary?.subscription_status ? (
                    <span className="text-zinc-500"> · {billingSummary.subscription_status}</span>
                  ) : null}
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-full border-zinc-700"
                    onClick={() => navigate('/subscription-plans')}
                  >
                    <CreditCard className="w-4 h-4 mr-2" /> Choose plan
                  </Button>
                  <Button
                    type="button"
                    disabled={billingBusy || !billingSummary?.has_stripe_customer}
                    className="rounded-full bg-white text-black"
                    onClick={async () => {
                      setBillingBusy(true);
                      try {
                        const token = localStorage.getItem('token');
                        const res = await fetch(`${backendUrl}/api/subscription/billing/portal`, {
                          method: 'POST',
                          headers: { Authorization: `Bearer ${token}` },
                        });
                        const data = await res.json().catch(() => ({}));
                        if (!res.ok) throw new Error(formatApiDetail(data.detail) || 'Portal unavailable');
                        window.location.href = data.url;
                      } catch (e) {
                        toast.error(e.message || 'Could not open billing portal');
                      } finally {
                        setBillingBusy(false);
                      }
                    }}
                  >
                    Manage billing
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={billingBusy}
                    className="rounded-full border-zinc-700 text-amber-200"
                    onClick={async () => {
                      if (!window.confirm('Cancel your paid subscription? You will keep access until period end when using Stripe.')) return;
                      setBillingBusy(true);
                      try {
                        const token = localStorage.getItem('token');
                        const res = await fetch(`${backendUrl}/api/subscription/billing/cancel`, {
                          method: 'POST',
                          headers: { Authorization: `Bearer ${token}` },
                        });
                        const data = await res.json().catch(() => ({}));
                        if (!res.ok) throw new Error(formatApiDetail(data.detail) || 'Cancel failed');
                        toast.success(data.message || 'Subscription canceled');
                        if (setUser && data.activation?.plan) {
                          setUser((prev) => ({ ...prev, snagcart_plan: data.activation.plan }));
                        }
                      } catch (e) {
                        toast.error(e.message || 'Cancel failed');
                      } finally {
                        setBillingBusy(false);
                      }
                    }}
                  >
                    Cancel subscription
                  </Button>
                </div>
                {!billingSummary?.has_stripe_customer && (
                  <p className="text-xs text-zinc-500">Stripe Customer Portal unlocks after a live subscription checkout.</p>
                )}
              </div>

              <div className="glass p-8 rounded-2xl space-y-4">
                <h2 className="text-2xl font-heading font-bold">Your data</h2>
                <p className="text-sm text-zinc-400">
                  Download a copy of your account data, or permanently delete your account.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button
                    type="button"
                    className="rounded-full bg-white text-black"
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        const res = await fetch(`${backendUrl}/api/auth/me/export`, {
                          headers: { Authorization: `Bearer ${token}` },
                        });
                        if (!res.ok) throw new Error('Export failed');
                        const data = await res.json();
                        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `snagcart-export-${Date.now()}.json`;
                        a.click();
                        URL.revokeObjectURL(url);
                        toast.success('Export downloaded');
                      } catch (e) {
                        toast.error(e.message || 'Export failed');
                      }
                    }}
                  >
                    Download my data
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-full border-red-500/40 text-red-300"
                    onClick={async () => {
                      if (!window.confirm('Delete your account? This cannot be undone from the UI.')) return;
                      try {
                        const token = localStorage.getItem('token');
                        const res = await fetch(`${backendUrl}/api/auth/me`, {
                          method: 'DELETE',
                          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                          body: JSON.stringify({ confirm: true }),
                        });
                        const data = await res.json().catch(() => ({}));
                        if (!res.ok) throw new Error(formatApiDetail(data.detail) || 'Delete failed');
                        localStorage.removeItem('token');
                        toast.success('Account deleted');
                        navigate('/auth');
                      } catch (e) {
                        toast.error(e.message || 'Delete failed');
                      }
                    }}
                  >
                    Delete account
                  </Button>
                </div>
                <p className="text-xs text-zinc-500">
                  Related: <Link to="/privacy" className="text-violet-400 hover:underline">Privacy</Link>
                  {' · '}
                  <Link to="/terms" className="text-violet-400 hover:underline">Terms</Link>
                  {' · '}
                  <Link to="/refund" className="text-violet-400 hover:underline">Refund</Link>
                </p>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="whitelabel">
            <WhiteLabelSettingsPanel user={user} />
          </TabsContent>

          {/* ── Support Notifications tab (Growth plan) ─────────────────── */}
          {(user?.snagcart_plan === 'growth' || user?.is_platform_admin) && (
            <TabsContent value="support-notifications">
              <SupportNotificationsPanel />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
    </DashboardShell>
  );
};


// ── Support Notifications Panel ───────────────────────────────────────────────
const SupportNotificationsPanel = () => {
  const backendUrl = API_BASE;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [settings, setSettings] = useState({
    support_notification_email: '',
    support_provider: 'smtp',
    // Common from-fields shared by all providers
    support_smtp_from_email: '',
    support_smtp_from_name: 'Support',
    // SMTP
    support_smtp_host: '',
    support_smtp_port: 587,
    support_smtp_username: '',
    support_smtp_password: '',
    support_smtp_use_tls: true,
    support_smtp_password_set: false,
    // SendGrid
    support_sendgrid_api_key: '',
    support_sendgrid_api_key_set: false,
    // Mailgun
    support_mailgun_domain: '',
    support_mailgun_api_key: '',
    support_mailgun_api_key_set: false,
    // SES
    support_ses_access_key_id: '',
    support_ses_secret_access_key: '',
    support_ses_secret_access_key_set: false,
    support_ses_region: 'us-east-1',
  });

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${backendUrl}/api/auth/me/support-settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        // Merge server values but blank out write-only secret edit buffers
        setSettings(prev => ({
          ...prev,
          ...data,
          support_smtp_password: '',
          support_sendgrid_api_key: '',
          support_mailgun_api_key: '',
          support_ses_secret_access_key: '',
        }));
      } catch (e) { /* ignore */ } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!settings.support_notification_email.trim()) {
      toast.error('Please enter a notification email address.');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/auth/me/support-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(settings),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed to save');
      toast.success('Support notification settings saved!');
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    setTesting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/auth/me/support-settings/test-email`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Test email failed');
      toast.success(`Test email sent to ${data.to}`);
    } catch (err) {
      toast.error(err.message || 'Test email failed');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="glass p-8 rounded-2xl flex items-center gap-3 text-zinc-400">
        <SettingsLoader className="w-4 h-4 animate-spin" /> Loading settings...
      </div>
    );
  }

  return (
    <div className="glass p-8 rounded-2xl">
      <div className="mb-6">
        <h2 className="text-2xl font-heading font-bold flex items-center gap-2">
          <Bell className="w-5 h-5 text-violet-400" />
          Support Ticket Notifications
        </h2>
        <p className="text-zinc-400 text-sm mt-1">
          When your users submit a support ticket, an email notification will be sent to the address you configure here.
          This is a per-workspace setting — each Growth tenant can configure their own.
        </p>
      </div>

      <form onSubmit={handleSave} className="space-y-6 max-w-xl">
        {/* Notification email */}
        <div className="border border-violet-500/20 rounded-xl p-5 bg-violet-500/5">
          <div className="flex items-center gap-2 mb-3">
            <Mail className="w-4 h-4 text-violet-400" />
            <span className="text-sm font-semibold text-violet-300">Notification Email</span>
          </div>
          <p className="text-xs text-zinc-400 mb-3">
            Every new support ticket from your users will trigger an email to this address. Pick a provider below and add the credentials needed to send.
          </p>
          <div>
            <Label className="text-sm mb-1 block">Receive tickets at</Label>
            <Input
              type="email"
              value={settings.support_notification_email}
              onChange={(e) => setSettings(s => ({ ...s, support_notification_email: e.target.value }))}
              placeholder="you@yourcompany.com"
              className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 rounded-xl h-11"
            />
          </div>
        </div>

        {/* Provider chooser */}
        <div className="border border-zinc-800 rounded-xl p-5 space-y-4 bg-black/30">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-semibold text-zinc-200">Email provider</Label>
              <p className="text-xs text-zinc-500 mt-1">Pick how the platform should deliver these notifications.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="support-provider-chooser">
            {[
              { id: 'smtp',     label: 'SMTP',     hint: 'Gmail / Office 365 / custom' },
              { id: 'sendgrid', label: 'SendGrid', hint: 'API key' },
              { id: 'mailgun',  label: 'Mailgun',  hint: 'Domain + API key' },
              { id: 'ses',      label: 'Amazon SES', hint: 'AWS keys' },
            ].map(p => (
              <button
                type="button"
                key={p.id}
                data-testid={`support-provider-${p.id}`}
                onClick={() => setSettings(s => ({ ...s, support_provider: p.id }))}
                className={`text-left p-3 rounded-lg border transition-all ${
                  settings.support_provider === p.id
                    ? 'border-violet-500 bg-violet-500/10'
                    : 'border-zinc-800 bg-zinc-900/40 hover:border-zinc-600'
                }`}
              >
                <div className="text-sm font-semibold text-white">{p.label}</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">{p.hint}</div>
              </button>
            ))}
          </div>

          {/* Shared "from" fields — used by every provider */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
            <div>
              <Label className="text-sm mb-1 block">From Email</Label>
              <Input
                value={settings.support_smtp_from_email}
                onChange={(e) => setSettings(s => ({ ...s, support_smtp_from_email: e.target.value }))}
                placeholder="support@yourcompany.com"
                data-testid="support-from-email"
                className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 rounded-xl h-10 text-sm"
              />
            </div>
            <div>
              <Label className="text-sm mb-1 block">From Name</Label>
              <Input
                value={settings.support_smtp_from_name}
                onChange={(e) => setSettings(s => ({ ...s, support_smtp_from_name: e.target.value }))}
                placeholder="Acme Support"
                data-testid="support-from-name"
                className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 rounded-xl h-10 text-sm"
              />
            </div>
          </div>

          {/* ─── SMTP ─── */}
          {settings.support_provider === 'smtp' && (
            <div className="space-y-4 pt-2" data-testid="support-provider-smtp-fields">
              <p className="text-[11px] text-zinc-500">
                For Gmail use an{' '}
                <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-violet-400 hover:underline">App Password</a>{' '}
                — your normal password will not work.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm mb-1 block">SMTP Host</Label>
                  <Input
                    value={settings.support_smtp_host}
                    onChange={(e) => setSettings(s => ({ ...s, support_smtp_host: e.target.value }))}
                    placeholder="smtp.gmail.com"
                    data-testid="support-smtp-host"
                    className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 rounded-xl h-10 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-sm mb-1 block">Port</Label>
                  <Input
                    type="number"
                    value={settings.support_smtp_port}
                    onChange={(e) => setSettings(s => ({ ...s, support_smtp_port: parseInt(e.target.value) || 587 }))}
                    placeholder="587"
                    data-testid="support-smtp-port"
                    className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 rounded-xl h-10 text-sm"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm mb-1 block">Username</Label>
                <Input
                  value={settings.support_smtp_username}
                  onChange={(e) => setSettings(s => ({ ...s, support_smtp_username: e.target.value }))}
                  placeholder="you@gmail.com"
                  data-testid="support-smtp-username"
                  className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 rounded-xl h-10 text-sm"
                />
              </div>
              <div>
                <Label className="text-sm mb-1 block">
                  Password / App Password
                  {settings.support_smtp_password_set && (
                    <span className="ml-2 text-[10px] text-emerald-400 font-normal">● saved</span>
                  )}
                </Label>
                <div className="relative">
                  <Input
                    type={showSmtpPassword ? 'text' : 'password'}
                    value={settings.support_smtp_password}
                    onChange={(e) => setSettings(s => ({ ...s, support_smtp_password: e.target.value }))}
                    placeholder={settings.support_smtp_password_set ? '••••••••  (leave blank to keep)' : 'App password'}
                    data-testid="support-smtp-password"
                    className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 rounded-xl h-10 text-sm pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSmtpPassword(v => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                  >
                    {showSmtpPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.support_smtp_use_tls}
                  onChange={(e) => setSettings(s => ({ ...s, support_smtp_use_tls: e.target.checked }))}
                  className="rounded"
                />
                Use STARTTLS (recommended for port 587)
              </label>
            </div>
          )}

          {/* ─── SendGrid ─── */}
          {settings.support_provider === 'sendgrid' && (
            <div className="space-y-3 pt-2" data-testid="support-provider-sendgrid-fields">
              <p className="text-[11px] text-zinc-500">
                Create an API key with <span className="text-zinc-300">Mail Send</span> permission at{' '}
                <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noreferrer" className="text-violet-400 hover:underline">SendGrid → API Keys</a>.
                The from-email above must be verified as a sender.
              </p>
              <div>
                <Label className="text-sm mb-1 block">
                  SendGrid API Key
                  {settings.support_sendgrid_api_key_set && (
                    <span className="ml-2 text-[10px] text-emerald-400 font-normal">● saved</span>
                  )}
                </Label>
                <Input
                  type="password"
                  value={settings.support_sendgrid_api_key}
                  onChange={(e) => setSettings(s => ({ ...s, support_sendgrid_api_key: e.target.value }))}
                  placeholder={settings.support_sendgrid_api_key_set ? '••••••••  (leave blank to keep)' : 'SG.xxxxxxxxxxxxx'}
                  data-testid="support-sendgrid-key"
                  className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 rounded-xl h-10 text-sm"
                />
              </div>
            </div>
          )}

          {/* ─── Mailgun ─── */}
          {settings.support_provider === 'mailgun' && (
            <div className="space-y-3 pt-2" data-testid="support-provider-mailgun-fields">
              <p className="text-[11px] text-zinc-500">
                Find these at{' '}
                <a href="https://app.mailgun.com/app/sending/domains" target="_blank" rel="noreferrer" className="text-violet-400 hover:underline">Mailgun → Sending → Domains</a>.
                Use the Private API key.
              </p>
              <div>
                <Label className="text-sm mb-1 block">Sending Domain</Label>
                <Input
                  value={settings.support_mailgun_domain}
                  onChange={(e) => setSettings(s => ({ ...s, support_mailgun_domain: e.target.value }))}
                  placeholder="mg.yourcompany.com"
                  data-testid="support-mailgun-domain"
                  className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 rounded-xl h-10 text-sm"
                />
              </div>
              <div>
                <Label className="text-sm mb-1 block">
                  Mailgun API Key
                  {settings.support_mailgun_api_key_set && (
                    <span className="ml-2 text-[10px] text-emerald-400 font-normal">● saved</span>
                  )}
                </Label>
                <Input
                  type="password"
                  value={settings.support_mailgun_api_key}
                  onChange={(e) => setSettings(s => ({ ...s, support_mailgun_api_key: e.target.value }))}
                  placeholder={settings.support_mailgun_api_key_set ? '••••••••  (leave blank to keep)' : 'key-xxxxxxxxxxxxx'}
                  data-testid="support-mailgun-key"
                  className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 rounded-xl h-10 text-sm"
                />
              </div>
            </div>
          )}

          {/* ─── Amazon SES ─── */}
          {settings.support_provider === 'ses' && (
            <div className="space-y-3 pt-2" data-testid="support-provider-ses-fields">
              <p className="text-[11px] text-zinc-500">
                Create an IAM user with the <span className="text-zinc-300">AmazonSESFullAccess</span> policy in your{' '}
                <a href="https://console.aws.amazon.com/iam/home#/users" target="_blank" rel="noreferrer" className="text-violet-400 hover:underline">AWS IAM Console</a>.
                The from-email must be verified in SES.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm mb-1 block">Access Key ID</Label>
                  <Input
                    value={settings.support_ses_access_key_id}
                    onChange={(e) => setSettings(s => ({ ...s, support_ses_access_key_id: e.target.value }))}
                    placeholder="AKIA…"
                    data-testid="support-ses-access-key"
                    className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 rounded-xl h-10 text-sm"
                  />
                </div>
                <div>
                  <Label className="text-sm mb-1 block">Region</Label>
                  <Input
                    value={settings.support_ses_region}
                    onChange={(e) => setSettings(s => ({ ...s, support_ses_region: e.target.value }))}
                    placeholder="us-east-1"
                    data-testid="support-ses-region"
                    className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 rounded-xl h-10 text-sm"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm mb-1 block">
                  Secret Access Key
                  {settings.support_ses_secret_access_key_set && (
                    <span className="ml-2 text-[10px] text-emerald-400 font-normal">● saved</span>
                  )}
                </Label>
                <Input
                  type="password"
                  value={settings.support_ses_secret_access_key}
                  onChange={(e) => setSettings(s => ({ ...s, support_ses_secret_access_key: e.target.value }))}
                  placeholder={settings.support_ses_secret_access_key_set ? '••••••••  (leave blank to keep)' : 'wJalrX…'}
                  data-testid="support-ses-secret-key"
                  className="bg-zinc-900/50 border-zinc-800 focus:border-violet-500 rounded-xl h-10 text-sm"
                />
              </div>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-4 pt-2">
          <Button
            type="submit"
            disabled={saving}
            className="bg-violet-600 hover:bg-violet-500 rounded-full px-8 flex items-center gap-2"
          >
            {saving ? <SettingsLoader className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
          <Button
            type="button"
            variant="outline"
            disabled={testing || !settings.support_notification_email}
            onClick={handleTestEmail}
            className="rounded-full px-6 border-zinc-700 text-zinc-300 hover:border-violet-500 hover:text-violet-300 flex items-center gap-2"
          >
            <Send className="w-4 h-4" />
            {testing ? 'Sending...' : 'Send Test Email'}
          </Button>
        </div>

        {settings.support_notification_email && (
          <p className="text-xs text-zinc-500 flex items-center gap-1">
            <Mail className="w-3 h-3" />
            Ticket notifications will be sent to{' '}
            <span className="text-violet-400 font-medium">{settings.support_notification_email}</span>
          </p>
        )}
      </form>
    </div>
  );
};


export default Settings;